/**
 * Tenant-aware URL builder — the emitter counterpart to `parseHost` /
 * `resolveTenantRewrite`.
 *
 * Under subdomain routing (default when the app URL is a recognised apex):
 *
 *     https://<slug>.solution.co.uk/admin/login
 *     https://<slug>.solution.co.uk/customer/login
 *     https://<slug>.solution.co.uk/r
 *
 * Under path-based routing (fallback for localhost, previews, or any
 * `appUrl` whose host is not a recognised apex):
 *
 *     https://solution.co.uk/admin/login/<slug>
 *     https://solution.co.uk/customer/login/<slug>
 *     https://solution.co.uk/r/<slug>
 *
 * `buildTenantUrl` picks the right form so every URL builder in the codebase
 * (email links, invite comms, sign-out redirects, referral URLs) goes
 * through a single decision point.
 */

import { getConfiguredBaseHosts, parseHost } from './parseHost';

export type TenantUrlMode = 'auto' | 'path' | 'subdomain';

export interface BuildTenantUrlOptions {
  /** The tenant slug (root parent for /admin, child for /customer & /r). */
  slug: string;
  /** Absolute base URL of the app (e.g. `https://solution.co.uk`). */
  appUrl: string;
  /**
   * Subdomain-form pathname — the path a browser would see after the
   * middleware rewrite (e.g. `/admin/login`, `/customer/login`, `/r`).
   * MUST start with `/` and MUST NOT include the slug.
   */
  path: string;
  /** Optional query string (without leading `?`). */
  query?: string;
  /**
   * Force a specific form. Defaults to `auto` (subdomain iff `appUrl`'s
   * host is a recognised apex base host).
   */
  mode?: TenantUrlMode;
}

/**
 * Build the tenant-facing URL, choosing subdomain vs path form based on
 * the shape of `appUrl`.
 *
 * When `mode='auto'` the subdomain form is used only if `appUrl`'s host is
 * a recognised apex (`parseHost.isApex` and matches a configured base).
 * If `appUrl` is `http://localhost:3000`, a preview URL, or already a
 * tenant subdomain, we fall back to the path form so nothing accidentally
 * routes to a non-existent host.
 *
 * The output is always an absolute URL (protocol + host + path[+ query]).
 */
export function buildTenantUrl(opts: BuildTenantUrlOptions): string {
  const { slug, appUrl, path, query, mode = 'auto' } = opts;

  if (!slug || !path || !path.startsWith('/')) {
    return withQuery(trimTrailingSlash(appUrl), query);
  }

  const useSubdomain =
    mode === 'subdomain' ||
    (mode === 'auto' && appUrlIsRecognisedApex(appUrl));

  if (useSubdomain) {
    try {
      const url = new URL(appUrl);
      const bases = getConfiguredBaseHosts();
      const parsed = parseHost(url.host, bases);
      // When mode is forced to `subdomain` but appUrl is not a recognised
      // apex, fall through to the path form — safer than emitting a host we
      // know is wrong. `parseHost` returns `isApex: true` for unrecognised
      // hosts too (localhost, IPs), so cross-check against the configured
      // base host list.
      if (parsed.isApex && bases.includes(parsed.baseHost)) {
        url.hostname = `${slug}.${parsed.baseHost}`;
        url.pathname = path;
        url.search = '';
        const base = stripTrailingSlash(url.toString());
        return withQuery(base, query);
      }
    } catch {
      // fall through to path form
    }
  }

  const base = `${trimTrailingSlash(appUrl)}${path}/${slug}`;
  return withQuery(base, query);
}

/**
 * True when `appUrl` parses to a URL whose host is one of the configured
 * apex base hosts (i.e. safe to prepend a tenant label to). Returns false
 * for `localhost`, IPs, preview URLs, and hosts that already carry a
 * subdomain.
 */
export function appUrlIsRecognisedApex(appUrl: string): boolean {
  try {
    const url = new URL(appUrl);
    const bases = getConfiguredBaseHosts();
    const parsed = parseHost(url.host, bases);
    return parsed.isApex && bases.includes(parsed.baseHost);
  } catch {
    return false;
  }
}

/**
 * Allow-list of hosts a client-side hard-navigation is permitted to reach
 * when we cross an origin (e.g. apex → tenant subdomain). Each configured
 * base host contributes the apex itself and a single-level wildcard, matching
 * production TLS certificate coverage. Consumed by `safeAssignLocation` via
 * its `allowedHosts` option.
 */
export function getTenantRedirectAllowedHosts(): string[] {
  const bases = getConfiguredBaseHosts();
  const out: string[] = [];
  for (const base of bases) {
    out.push(base, `*.${base}`);
  }
  return out;
}

/**
 * Resolve the app's own origin — first from `NEXT_PUBLIC_APP_URL`, else
 * from `window.location.origin` on the client. Returns `null` when neither
 * is available (SSR without the env set).
 */
export function getAppOrigin(): string | null {
  const fromEnv = process.env.NEXT_PUBLIC_APP_URL?.trim();
  if (fromEnv) return trimTrailingSlash(fromEnv);
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return null;
}

function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

function stripTrailingSlash(s: string): string {
  return s.replace(/\/$/, '');
}

function withQuery(base: string, query?: string): string {
  if (!query) return base;
  return `${base}?${query}`;
}
